/**
 * The long text field as it renders directly on a form (ControlUI's non-table
 * branch for longText, richText, json, xml, html and ipAddress).
 * The textarea grows with its content up to MAX_ROWS, then scrolls; the person
 * can collapse it to a preview of its first PREVIEW_LINES lines and expand it
 * again. Collapsing is view-only: it never calls onChange, and the expanded
 * textarea always edits the whole value. A stored value of any type renders as
 * text (SYD-3030): the textarea and the preview both read the same coerced
 * string, so onChange still reports a string. An object is rendered as JSON
 * rather than through String, which would show `[object Object]` and overwrite
 * the stored object with that text on the first keystroke — the json branch
 * reaches this component through an unmapped field whose value is an object.
 */
import { FC, useState } from 'react';
import { Button, Input, Typography } from 'antd';
import type { GetProps } from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';

import { MAX_LENGTHS, MIN_LENGTHS } from '#types/System.Parameters.types';
import FlexCol from '@/components/atoms/FlexCol';

const { Text } = Typography;

const MAX_ROWS = 10;
const PREVIEW_LINES = 3;

type LongTextUIProps = Omit<GetProps<typeof Input.TextArea>, 'value'> & {
    /** A stored value is not always text: an imported phone number arrives as a number. */
    value?: unknown;
};

const asText = (value: unknown): string => {
    if (value === undefined || value === null) return '';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
};

const LongTextUI: FC<LongTextUIProps> = ({ value, ...textAreaProps }) => {
    const [collapsed, setCollapsed] = useState(false);
    const text = asText(value);
    const preview = text.split('\n').slice(0, PREVIEW_LINES).join('\n');

    return (
        <FlexCol gap={5}>
            {collapsed
                ? <Text type="secondary" style={{ whiteSpace: 'pre-wrap' }}>{preview}</Text>
                : <Input.TextArea
                    {...textAreaProps}
                    value={text}
                    maxLength={MAX_LENGTHS.longText}
                    minLength={MIN_LENGTHS.longText}
                    autoComplete="off"
                    autoSize={{ maxRows: MAX_ROWS }}
                />}
            <Button
                type="link"
                size="small"
                icon={collapsed ? <DownOutlined /> : <UpOutlined />}
                aria-label={`${collapsed ? 'Expand' : 'Collapse'} ${textAreaProps.id ?? ''}`.trim()}
                onClick={() => setCollapsed(!collapsed)}
            >
                {collapsed ? 'Expand' : 'Collapse'}
            </Button>
        </FlexCol>
    );
};

export default LongTextUI;
